/**
 * Annuaire "villes desservies" — grille de liens internes affichée en bas des
 * hubs /chauffeur et /location. Renforce le maillage : chaque page ville reçoit
 * un lien depuis une page hub (forte autorité). Données via cityDirectory.ts.
 */
import Link from "next/link";
import type { Locale } from "@/config/i18n";
import type { CityLink } from "@/utils/cityDirectory";

type Kind = "chauffeur" | "location" | "events";

const HEADING: Record<Kind, Record<Locale, string>> = {
  chauffeur: {
    fr: "Chauffeur privé — villes desservies",
    en: "Private chauffeur — cities we serve",
    ar: "سائق خاص — المدن التي نخدمها",
  },
  location: {
    fr: "Location de voiture — villes desservies",
    en: "Car rental — cities we serve",
    ar: "تأجير السيارات — المدن التي نخدمها",
  },
  events: {
    fr: "Événements Côte d'Azur — chauffeur & transferts",
    en: "French Riviera events — chauffeur & transfers",
    ar: "فعاليات الريفييرا — سائق ونقل",
  },
};

const INTRO: Record<Kind, Record<Locale, string>> = {
  chauffeur: {
    fr: "Chauffeur privé & VTC dans toute la Côte d’Azur et le Var. Choisissez votre ville :",
    en: "Private chauffeur & VTC across the French Riviera and the Var. Pick your city:",
    ar: "سائق خاص وVTC في كامل الريفييرا الفرنسية والفار. اختر مدينتك:",
  },
  location: {
    fr: "Location de voiture avec ou sans chauffeur dans toute la région. Choisissez votre ville :",
    en: "Car rental with or without a chauffeur across the region. Pick your city:",
    ar: "تأجير سيارة مع سائق أو بدونه في كامل المنطقة. اختر مدينتك:",
  },
  events: {
    fr: "Festivals, régates, salons et grands prix : votre chauffeur privé pour chaque grand rendez-vous de la Riviera.",
    en: "Festivals, regattas, trade shows and grands prix: your private chauffeur for every major Riviera event.",
    ar: "مهرجانات وسباقات ومعارض وجوائز كبرى: سائقك الخاص لكل حدث كبير على الريفييرا.",
  },
};

type Props = { kind: Kind; cities: CityLink[]; locale?: Locale };

export function CitiesDirectory({
  kind,
  cities,
  locale = "fr",
}: Props): JSX.Element {
  return (
    <section className="border-t border-black/10 bg-white py-14" lang={locale}>
      <div className="mx-auto max-w-[90rem] px-6 md:px-8">
        <h2 className="font-space-age text-lg tracking-[0.2em] text-black/90">
          {HEADING[kind][locale]}
        </h2>
        <p className="mt-2 max-w-3xl text-sm text-black/60">
          {INTRO[kind][locale]}
        </p>
        <ul className="mt-6 grid grid-cols-2 gap-x-4 gap-y-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6">
          {cities.map((c) => (
            <li key={c.href}>
              <Link
                href={c.href}
                className="block rounded-md px-2 py-1.5 text-sm text-black/70 transition hover:bg-black/[0.04] hover:text-primary"
              >
                {c.label}
              </Link>
            </li>
          ))}
        </ul>
      </div>
    </section>
  );
}

export default CitiesDirectory;
